import {
  collection,
  Timestamp,
  setDoc,
  addDoc,
  doc,
  getDoc,
  getDocs,
  deleteDoc,
  query,
  where,
  writeBatch,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebaseClient";

// Use this to update root properties on documents in the firestore.
// auth: Need to pass the auth provider. Unable to access react provider from non react class. Should rethink this.
// firestoreRef: Firestore document reference
// propObj: Is an object containing the root property you want to update on the document.
// See https://firebase.google.com/docs/firestore/manage-data/add-data?authuser=0#web-version-9_1
async function mergeDocument(auth, firestoreRef, propObj) {
  const ref =
    typeof firestoreRef === "string" ? doc(db, firestoreRef) : firestoreRef;

  try {
    const readyForUpdate = applyAuditsToUpdate(auth, propObj);
    await setDoc(ref, readyForUpdate, { merge: true });
  } catch (error) {
    console.log(error.toString());
  }
}

/**
 * Create a firestore DocumentReference from a path.
 * @param {string} path - The path to the document.
 * @returns {DocumentReference} - The document reference.
 */
function getDocumentRef(path) {
  const ref = doc(db, path);
  return ref;
}

async function getDocumentByPath(path) {
  const ref = doc(db, path);
  const docSnap = await getDoc(ref);

  return { ...docSnap.data(), ref: docSnap.ref } || {};
}

async function getDocumentByRef(ref) {
  const docSnap = await getDoc(ref);
  return docSnap.data();
}

async function deleteDocumentByRef(ref) {
  await deleteDoc(ref);
}

/**
 * Use this to get all documents from a collection or a subcollection Ordered by createdAtUtc desc.
 * @param {*} path Collection path like 'products' or 'products/1234/subcollection'
 * @param {*} getCountOnly Defaults to false to return all the documents in the collection. If true is passed,
 * will return the number of documents in the collection.
 */
const getCollections = async (path, getCountOnly = false) => {
  const ref = collection(db, path);
  const docQuery = query(ref, orderBy("createdAtUtc", "desc"));

  const querySnapshot = await getDocs(docQuery);

  if (getCountOnly) {
    return querySnapshot.size;
  }

  return querySnapshot.docs.map((doc) => doc.data());
};

/**
 * Takes a list of newly created documents.
 * Creates a primary id and adds it as the sigilId.
 * Performs batch inserts into firestore, 500 (firestore limit) at a time.
 * https://firebase.google.com/docs/firestore/manage-data/transactions#batched-writes
 * @param {string} collectionName
 * @param {array{objects}} docs
 */
const bulkNew = async (collectionName, docs) => {
  //Save new products in chuncks of 500.
  const chunkSize = 499;
  for (let i = 0; i < docs.length; i += chunkSize) {
    const batch = writeBatch(db);
    const chunkStart = i;
    const chunkEnd = i + chunkSize;

    for (let j = chunkStart; j < chunkEnd && j < docs.length; j++) {
      const ref = doc(collection(db, collectionName));
      docs[j].sigilId = ref;
      batch.set(ref, docs[j]);
    }

    await batch.commit();

    //const chunks = docs.slice(i, i + chunkSize);

    // chunks.forEach((chunk) => {
    //   const ref = doc(collection(db, collectionName));
    //   chunk.sigilId = ref;
    //   batch.set(ref, chunk);
    // });
  }
};

/**
 * Takes a list of updates, applies audits, and peforms updates.
 * @param {Array} An array of an object with a ref and update. Example: [{ref: doc(db, 'products/1234'), update: {name: 'new name'}}]
 */
const bulkUpdate = async (auth, docs) => {
  const chunkSize = 499;
  for (let i = 0; i < docs.length; i += chunkSize) {
    const chunks = docs.slice(i, i + chunkSize);
    const batch = writeBatch(db);

    chunks.forEach((chunk) => {
      const readyForUpdate = applyAuditsToUpdate(auth, chunk.update);
      batch.update(chunk.ref, readyForUpdate);
    });

    await batch.commit();
  }
};

/**
 * Add a new document to a subcollection.
 * @param {*} parentRef
 * @param {*} document
 * @returns
 */
async function addSubCollection(auth, subCollectionName, parentRef, document) {
  const ref = collection(db, parentRef.path, subCollectionName);
  const resultRef = await addDoc(ref, document.toObject());
  const update = { sigilId: resultRef };
  await mergeDocument(auth, resultRef, update);
  return { ...document, ...update };
}

//uses the query and where import

//just add a parameter called collections and we can use 'sellers' or 'products'. Also do specific brand so it is dynamic
const getSpecificCollection = async (type, brand) => {
  const brandCollection = query(
    collection(db, type),
    where("brand", "==", brand),
    where("tenant", "==", brand)
  );

  const querySnapShot = await getDocs(brandCollection);

  return querySnapShot.docs.map((doc) => doc.data());
};

function applyAuditsToUpdate(auth, rootDoc) {
  const audits = {
    updatedAtUtc: Timestamp.now(),
    lastModifiedBy: auth.email || "portal",
  };

  return { ...rootDoc, ...audits };
}

function firebaseTimestampFromDate(date) {
  return Timestamp.fromDate(date);
}

export {
  addSubCollection,
  bulkNew,
  bulkUpdate,
  mergeDocument,
  deleteDocumentByRef,
  getDocumentByPath,
  getDocumentByRef,
  getCollections,
  getSpecificCollection,
  applyAuditsToUpdate,
  getDocumentRef,
  firebaseTimestampFromDate,
};
